import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import moment from "moment"

import { Pagination } from "components"
import Table from "components/table"
import Label from "components/forms/label"
import Input from "components/forms/input"
import { HO_OUTLET_IDS } from "helpers/general-helper"
import SelectSearchable from "components/forms/select-searchable"

import useBranchRepositories from "repositories/branch"
import useStoreBalanceStockRepositories from "repositories/store-balance-stock"

// Angka bulat (mis. jumlah unit produk) — selalu tanpa desimal.
const formatInt = (value) => Number(value ?? 0).toLocaleString("id-ID")

export default function List() {
  const [searchParams, setSearchParams] = useSearchParams()

  // outlet_ids = daftar id outlet dipisah koma; kosong => semua outlet.
  const outletIds = searchParams.get("outlet_ids") || ""
  const selectedOutletIds = useMemo(
    () => (outletIds ? outletIds.split(",").filter(Boolean) : []),
    [outletIds]
  )
  const month = searchParams.get("month") || moment().format("YYYY-MM")
  const productGroupSearch = searchParams.get("product_group_search") || ""
  // 230726 (permintaan user): pencarian terpisah per UNIT (nama unit / SKU).
  const productUnitSearch = searchParams.get("product_unit_search") || ""
  const page = searchParams.get("page") || "1"
  const perPage = searchParams.get("per_page") || "20"
  // 240726 (permintaan user): satuan tampilan stok (PCS/LUSIN/PASANG). Non-PCS = 2 desimal.
  const unit = (searchParams.get("unit") || "PCS").toUpperCase()
  const formatNumber = (value) =>
    Number(value ?? 0).toLocaleString("id-ID", unit === "PCS" ? {} : { minimumFractionDigits: 2, maximumFractionDigits: 2 })

  const [searchInput, setSearchInput] = useState(productGroupSearch)
  const [unitSearchInput, setUnitSearchInput] = useState(productUnitSearch)

  // Outlet opsional (kosong = semua outlet). Cukup periode bulan sudah bisa fetch.
  const isReadyToFetch = !!month

  useEffect(() => {
    setSearchInput(productGroupSearch)
  }, [productGroupSearch])

  useEffect(() => {
    setUnitSearchInput(productUnitSearch)
  }, [productUnitSearch])

  const submitSearchParam = (key, value, currentValue) => {
    if (value === currentValue) return

    const nextParams = new URLSearchParams(searchParams)

    if (value.trim() === "") {
      nextParams.delete(key)
    } else {
      nextParams.set(key, value)
    }

    nextParams.set("page", "1")
    setSearchParams(nextParams)
  }

  const submitSearch = () => submitSearchParam("product_group_search", searchInput, productGroupSearch)
  const submitUnitSearch = () => submitSearchParam("product_unit_search", unitSearchInput, productUnitSearch)

  // 230726 (rule entity): Stock Real terbuka lintas entitas — daftar outlet LENGKAP.
  const { data: branchSelection, isLoading: isLoadingBranchSelection } =
    useBranchRepositories({ paginate: false, stockScope: true })

  const branchOptions = useMemo(() => {
    // 250726 (permintaan user): opsi outlet diurut ASC by outlet id.
    const list = Array.isArray(branchSelection) ? [...branchSelection] : []
    return list.sort((a, b) => (Number(a?.id) || 0) - (Number(b?.id) || 0))
  }, [branchSelection])

  const { data: reportData, isLoading } = useStoreBalanceStockRepositories({
    outlet_ids: outletIds || undefined,
    month: month || undefined,
    product_group_search: productGroupSearch || undefined,
    product_unit_search: productUnitSearch || undefined,
    unit: unit !== "PCS" ? unit : undefined,
    page,
    per_page: perPage,
    enabled: isReadyToFetch,
  })

  const items = useMemo(() => {
    if (Array.isArray(reportData?.data?.data)) return reportData.data.data
    if (Array.isArray(reportData?.data)) return reportData.data
    return reportData?.items?.data ?? []
  }, [reportData])

  const pagination = reportData?.data ?? reportData?.items
  const paginationLinks = useMemo(() => {
    const links = pagination?.links ?? []

    return links.map((link) => {
      if (!link?.url) return link

      const nextParams = new URLSearchParams(searchParams)

      try {
        const url = new URL(link.url, window.location.origin)
        const pageNumber = url.searchParams.get("page")

        if (pageNumber) {
          nextParams.set("page", pageNumber)
        }
      } catch (error) {
        const urlParams = new URLSearchParams(String(link.url).split("?")[1] ?? "")
        const pageNumber = urlParams.get("page")

        if (pageNumber) {
          nextParams.set("page", pageNumber)
        }
      }

      return {
        ...link,
        url: `?${nextParams.toString()}`,
      }
    })
  }, [pagination, searchParams])

  const paginationFrom = useMemo(() => {
    if (pagination?.from) return pagination.from
    if (!pagination || Number(pagination.total ?? 0) === 0) return 0

    return (Number(pagination.current_page ?? 1) - 1) * Number(pagination.per_page ?? perPage) + 1
  }, [pagination, perPage])

  const paginationTo = useMemo(() => {
    if (pagination?.to) return pagination.to
    if (!pagination) return 0

    return Math.min(
      Number(pagination.current_page ?? 1) * Number(pagination.per_page ?? perPage),
      Number(pagination.total ?? 0)
    )
  }, [pagination, perPage])

  const paginationTotal = pagination?.total

  const handleExportExcel = () => {
    if (!isReadyToFetch) return

    const params = new URLSearchParams()
    if (outletIds) params.set("outlet_ids", outletIds)
    if (month) params.set("month", month)
    if (productGroupSearch) params.set("product_group_search", productGroupSearch)
    if (productUnitSearch) params.set("product_unit_search", productUnitSearch)
    if (unit && unit !== "PCS") params.set("unit", unit)

    const url = `${process.env.REACT_APP_INVENTORY_API_URL}/reports/store-balance-stock/export?${params.toString()}`
    window.open(url, "_blank")
  }

  const mergeSearchParams = (nextValues) => {
    const nextParams = new URLSearchParams(searchParams)

    Object.entries(nextValues).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        nextParams.delete(key)
      } else {
        nextParams.set(key, value)
      }
    })

    setSearchParams(nextParams)
  }

  const dataListHeaders = [
    { isAction: false, content: "Grup Produk" },
    { isAction: false, content: "Detail Produk" },
    // { isAction: false, content: "SKU" },
    { isAction: false, content: "Stok Awal" },
    { isAction: false, content: "Stok Masuk" },
    { isAction: false, content: "Stok Keluar" },
    { isAction: false, content: "Retur" },
    { isAction: false, content: "Kodin" },
    { isAction: false, content: "Kodout" },
    { isAction: false, content: "Stok Akhir" },
  ]

  const dataListColumns = [
    {
      type: "default",
      bold: true,
      content: (v) => (
        <div className="w-[200px] whitespace-normal break-words leading-5">
          {v.product_group_name || "-"}
        </div>
      ),
    },
    {
      type: "default",
      content: (v) => (
        <div className="w-[200px] whitespace-normal break-words leading-5">
          {v.product_unit_name || "-"}
        </div>
      ),
    },
    // {
    //   type: "default",
    //   content: (v) => v.sku || "-",
    // },
    {
      type: "default",
      content: (v) => (
        <span className="font-semibold text-sky-700">
          {formatNumber(v.opening_stock)}
        </span>
      ),
    },
    {
      type: "default",
      content: (v) => (
        <span className="font-semibold text-green-700">
          {formatNumber(v.stock_in)}
        </span>
      ),
    },
    {
      type: "default",
      content: (v) => (
        <span className="font-semibold text-red-700">
          {formatNumber(v.stock_out)}
        </span>
      ),
    },
    {
      type: "default",
      content: (v) => (
        <span className="font-semibold text-amber-700">
          {formatNumber(v.retur)}
        </span>
      ),
    },
    {
      type: "default",
      content: (v) => (
        <span className="font-semibold text-emerald-700">
          {formatNumber(v.kodin)}
        </span>
      ),
    },
    {
      type: "default",
      content: (v) => (
        <span className="font-semibold text-rose-700">
          {formatNumber(v.kodout)}
        </span>
      ),
    },
    {
      type: "default",
      content: (v) => (
        <span className="font-semibold text-neutral-900">
          {formatNumber(v.ending_stock)}
        </span>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <div className="p-5 bg-white border rounded-2xl border-neutral-200">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">
              Stock Real
            </h2>
            <p className="mt-1 text-sm text-neutral-500">
              Menampilkan stock real produk per outlet berdasarkan periode bulan.
            </p>
          </div>

          <div className="flex items-end gap-3 flex-wrap">
            <div className="w-72">
              <Label value="Outlet (bisa lebih dari satu)" />
              {/* ATALLA-297: multiselect — mis. HO + Gudang Titip Pabrik. Nilai dikirim
                  sebagai daftar id dipisah koma (backend whereIn). */}
              <SelectSearchable
                multiple
                selection={branchOptions}
                isLoading={isLoadingBranchSelection}
                value={selectedOutletIds}
                onChange={(values) => {
                  const list = Array.isArray(values) ? values : [values]
                  mergeSearchParams({ outlet_ids: list.filter(Boolean).join(",") || null, page: "1" })
                }}
                keyValue={(v) => v.id}
                title={(v) => v.name}
                description={(v) => v.code || v.id}
                placeholder="Pilih Outlet..."
              />
              {/* 290726 (permintaan user): pintasan "Semua Outlet" & "HO" — di filter
                  lain keduanya berupa opsi grup, tapi di sini filternya MULTI-SELECT
                  sehingga lebih tepat jadi tombol yang mengisi pilihan.
                  Semua Outlet = kosongkan filter (backend: outlet_ids kosong = semua). */}
              <div className="flex items-center gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => mergeSearchParams({ outlet_ids: null, page: "1" })}
                  className={`px-2.5 py-1 text-[11px] font-semibold rounded-lg border transition ${
                    selectedOutletIds.length === 0
                      ? "bg-brand-900 text-white border-brand-900"
                      : "bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50"
                  }`}
                >
                  Semua Outlet
                </button>
                <button
                  type="button"
                  onClick={() =>
                    mergeSearchParams({ outlet_ids: HO_OUTLET_IDS.join(","), page: "1" })
                  }
                  className={`px-2.5 py-1 text-[11px] font-semibold rounded-lg border transition ${
                    selectedOutletIds.length === HO_OUTLET_IDS.length &&
                    HO_OUTLET_IDS.every((id) => selectedOutletIds.includes(String(id)))
                      ? "bg-brand-900 text-white border-brand-900"
                      : "bg-white text-neutral-700 border-neutral-200 hover:bg-neutral-50"
                  }`}
                  title="HO + Gudang Titip Pabrik"
                >
                  HO
                </button>
              </div>
            </div>

            <div className="w-44">
              <Label value="Bulan" />
              <Input
                type="month"
                value={month}
                onChange={(e) => {
                  mergeSearchParams({ month: e.target.value, page: "1" })
                }}
              />
            </div>

            <div className="w-72">
              <Label value="Cari Product Group" />
              <Input
                type="text"
                value={searchInput}
                placeholder="Cari nama product group..."
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitSearch() } }}
              />
            </div>

            <div className="w-72">
              <Label value="Cari Unit" />
              <Input
                type="text"
                value={unitSearchInput}
                placeholder="Cari nama unit / SKU..."
                onChange={(e) => setUnitSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitUnitSearch() } }}
              />
            </div>

            <div className="w-36">
              <Label value="Satuan" />
              <select
                value={unit}
                onChange={(e) => mergeSearchParams({ unit: e.target.value === "PCS" ? null : e.target.value, page: "1" })}
                className="w-full px-3 py-3 text-sm border rounded-xl border-neutral-200 focus:outline-none focus:ring"
              >
                {["PCS", "LUSIN", "PASANG"].map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </div>

            <div className="w-32">
              <Label value="Per Halaman" />
              <select
                value={perPage}
                onChange={(e) => {
                  mergeSearchParams({ per_page: e.target.value, page: "1" })
                }}
                className="w-full px-3 py-3 text-sm border rounded-xl border-neutral-200 focus:outline-none focus:ring"
              >
                {[10, 20, 50, 100, 200].map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {!isReadyToFetch ? (
        <div className="p-6 bg-white border rounded-2xl border-neutral-200">
          <h3 className="text-base font-semibold text-neutral-900">
            Pilih periode terlebih dahulu
          </h3>
          <p className="mt-1 text-sm text-neutral-500">
            Data saldo stok tidak akan ditampilkan sebelum periode bulan dipilih. Kosongkan outlet untuk menampilkan semua outlet.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            {[
              {
                label: "Unit Produk",
                value: formatInt(reportData?.summary?.total_product_units),
                tone: "text-blue-700",
              },
              {
                label: "Stok Awal",
                value: formatNumber(reportData?.summary?.opening_stock),
                tone: "text-sky-700",
              },
              {
                label: "Stok Masuk",
                value: formatNumber(reportData?.summary?.stock_in),
                tone: "text-green-700",
              },
              {
                label: "Stok Keluar",
                value: formatNumber(reportData?.summary?.stock_out),
                tone: "text-red-700",
              },
              {
                label: "Retur",
                value: formatNumber(reportData?.summary?.retur),
                tone: "text-amber-700",
              },
              {
                label: "Kodin",
                value: formatNumber(reportData?.summary?.kodin),
                tone: "text-emerald-700",
              },
              {
                label: "Kodout",
                value: formatNumber(reportData?.summary?.kodout),
                tone: "text-rose-700",
              },
              {
                label: "Stok Akhir",
                value: formatNumber(reportData?.summary?.ending_stock),
                tone: "text-neutral-900",
              },
            ].map((card) => (
              <div
                key={card.label}
                className="p-4 border rounded-xl border-neutral-200 bg-neutral-50"
              >
                <div className="text-xs uppercase tracking-wide text-neutral-500">
                  {card.label}
                </div>
                <div className={`mt-2 text-2xl font-semibold ${card.tone}`}>
                  {isLoading ? "..." : card.value}
                </div>
              </div>
            ))}
          </div>

          <div className="p-5 bg-white border rounded-2xl border-neutral-200">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-lg font-semibold text-neutral-900">
                  Detail Stock Real
                </h2>
                <p className="mt-1 text-sm text-neutral-500">
                  Produk diurutkan A-Z berdasarkan nama unit (detail produk).
                </p>
              </div>

              <div className="flex items-center gap-4">
                <div className="text-sm text-neutral-500">
                  Periode:{" "}
                  <span className="font-medium text-neutral-800">
                    {month || "-"}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleExportExcel}
                  disabled={!isReadyToFetch || isLoading}
                  className="inline-flex items-center px-4 py-2 text-sm text-white transition bg-brand-800 rounded-xl active:hover:scale-95 disabled:opacity-50"
                >
                  Export Excel
                </button>
              </div>
            </div>

            <div className="mt-5">
              <Table
                isLoading={isLoading}
                data={items}
                headers={dataListHeaders}
                columns={dataListColumns}
                isEmpty={!items.length}
                isEmptyOnSearching={false}
                // 270726 (permintaan user): jarak antar kolom dirapatkan — tabel
                // Stock Real kolomnya banyak sehingga terlalu melebar.
                compact
              />

              <div className="mt-4">

                <Pagination
                  links={paginationLinks}
                  from={paginationFrom}
                  to={paginationTo}
                  total={paginationTotal}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
